import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import useBrains from '@/hooks/useBrains';

import { useCampaigns } from './useCampaigns';
import { useCampaignSubscribers } from '../hooks/useCampaignSubscribers';
import {
  setCsvLiveInstructionTargets,
  setCsvMappingData,
  setSelectedCampaign,
  updateDraftCampaign,
} from '../redux/actions';
import { selectCsvHeaders, selectSelectedCampaign } from '../redux/selectors';
import { reconstructCsvFromSubscribers } from '../utils';

/**
 * Hook to initialize campaign data in Redux store. Runs once from
 * `CampaignRouteWrapper` when entering any campaign route. Pulls the saved
 * campaign + subscribers and seeds the draft slice with their data.
 */
export const useInitializeCampaignRedux = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const dispatch = useDispatch();
  const { campaign } = useCampaigns(campaignId);
  const selectedCampaign = useSelector(selectSelectedCampaign);
  const csvHeaders = useSelector(selectCsvHeaders);

  const { campaign_subscribers, listStatus } = useCampaignSubscribers(campaignId);

  // Fetch brain data to get the brain name
  const { brain, getStatus: getBrainStatus } = useBrains(campaign?.brain_id);

  useEffect(() => {
    // Only process if we have all the data and haven't processed this campaign yet
    if (
      campaign &&
      listStatus === 'success' &&
      campaign_subscribers &&
      (!selectedCampaign || selectedCampaign.campaign_id !== campaign.campaign_id)
    ) {
      // Load campaign tags from subscribers_context and convert them to the expected format
      const savedTags = (campaign.subscribers_context?.tags as string[]) || [];
      const tagObjects = savedTags.map((tagValue) => ({ label: tagValue, value: tagValue }));

      // Restore the user's "copy to live_instructions" selection so the Match
      // Fields modal preselects the same boxes on next open.
      const savedLiveInstructionKeys = campaign.subscribers_context?.live_instructions_keys;
      if (Array.isArray(savedLiveInstructionKeys)) {
        dispatch(setCsvLiveInstructionTargets(savedLiveInstructionKeys as string[]));
      }

      // Rehydrate the CSV view from saved subscribers when Redux has none (e.g.
      // after a page refresh). The original upload lives only in Redux and is
      // lost on reload, which left the Match Fields modal unable to reopen on an
      // already-created campaign. Reconstruct a faithful, identity-mapped view
      // so the modal (and the audience-review re-map) work again.
      if ((!csvHeaders || csvHeaders.length === 0) && campaign_subscribers.length > 0) {
        const reconstructed = reconstructCsvFromSubscribers(campaign_subscribers);
        if (reconstructed) {
          dispatch(setCsvMappingData(reconstructed));
        }
      }

      // Update draft campaign with additional data
      dispatch(
        updateDraftCampaign({
          ...campaign,
          name: campaign.name,
          brain_parent_id: campaign.brain_parent_id,
          brain_version: campaign.brain_version,
          tags: tagObjects,
        })
      );

      dispatch(setSelectedCampaign(campaign));
    }
    // csvHeaders is read only to gate the one-time rehydration; the
    // selectedCampaign guard already prevents this effect from re-running, so a
    // later upload that sets csvHeaders won't re-trigger reconstruction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, campaign_subscribers, brain, dispatch, listStatus, campaignId, selectedCampaign]);

  // Separate useEffect for updating brain name when brain data becomes available
  useEffect(() => {
    if (campaign && brain?.name && getBrainStatus === 'success') {
      dispatch(
        updateDraftCampaign({
          brain_name: brain.name,
          brain_parent_id: campaign.brain_parent_id,
          brain_version: campaign.brain_version,
        })
      );
    }
  }, [campaign, brain, getBrainStatus, campaignId, dispatch]);
};
